import React, { useId, useState } from "react";
import toast from "react-hot-toast";
import { X, Play, AlertTriangle } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { Input } from "@/components/ui/Input";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { plural } from "@/utils/formatText";

import { CONFIRM_INPUT_CLASS } from "../constants";
import ErrorNote from "./ErrorNote";
import ResultList from "./ResultList";
import RevisionList from "./RevisionList";

// Alembic Apply modal (no preview, no stamp-only — mirrors RlsApplyModal in
// shape). The platform DB is deliberately not a target. A synthetic
// { key: 'all', isSyntheticAll: true } row lets the fleet button reuse this.
//
// For a single database it now lists the revisions the upgrade would run, named
// from their own migration files. "Up to a3f7c9d" is not something anybody can
// consent to. The fleet-wide target has no such list: each database is on its
// own revision, so what runs differs per database — same reason the RLS and
// permissions modals skip their previews for 'all'.
//
// `onRefresh` reloads the dashboard behind the modal. It runs after *every*
// attempt, success or not: a partial apply moved some databases forward, and
// leaving the table on the pre-apply snapshot reads as "nothing happened".
const AlembicApplyModal = ({
    row,
    alembicHead,
    catalog,
    onClose,
    onRefresh,
    onCompleted,
}) => {
    const [confirmText, setConfirmText] = useState("");
    const [applying, setApplying] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const confirmId = useId();

    const isAll = !!row?.isSyntheticAll || row?.key === "all";
    const target = isAll ? "all" : `tenant:${row?.tenantId ?? row?.key ?? ""}`;
    const expectedConfirm = isAll
        ? "APPLY ALL"
        : row?.tenantSlug || row?.dbName || "APPLY";

    const canApply = confirmText.trim() === expectedConfirm && !applying;

    // The row already carries its pending hashes; the catalogue turns them into
    // names and the operations each one runs. Only meaningful for a single
    // database — see the note above.
    const pending = isAll
        ? []
        : catalog?.describeRevisions(row?.pendingRevisions) ?? [];
    const pendingRawSql = pending.reduce(
        (total, revision) => total + (revision.upgradeRawSql ?? 0),
        0
    );

    const onApplyClick = async () => {
        if (!canApply) return;
        try {
            setApplying(true);
            setError(null);
            setResults(null);
            const data = await PlatformService.applyAlembic({ target });
            const rows = data?.results ?? [];
            setResults(rows);
            const succeeded = rows.filter((r) => r.success).length;
            const failed = rows.filter((r) => !r.success).length;
            if (failed > 0) {
                toast.error(
                    `${failed} database${failed > 1 ? "s" : ""} failed. Details below.`
                );
                // Some databases did move. Refresh the dashboard, keep the
                // modal open so the failures stay readable.
                onRefresh?.();
            } else {
                toast.success(
                    `Schema upgraded on ${succeeded} database${succeeded === 1 ? "" : "s"}.`
                );
                onCompleted?.();
            }
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={!!row} onOpenChange={(o) => !o && !applying && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[720px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        Upgrade the schema
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
                        Target: <span className="font-medium">{row?.label}</span>
                        {alembicHead && (
                            <>
                                {" "}
                                · up to{" "}
                                <span className="font-mono" title={alembicHead}>
                                    {alembicHead.slice(0, 7)}
                                </span>
                            </>
                        )}
                    </DialogDescription>
                    <DialogClose
                        disabled={applying}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    <Callout tone="warning" icon={AlertTriangle} size="xs">
                        <span className="font-medium block mb-0.5">Before you apply</span>
                        The platform database has to be on the code head already — run{" "}
                        <span className="font-mono">alembic upgrade head</span> in the
                        server shell first. This then carries the same revision to every
                        dedicated tenant database in one pass. A database that fails does
                        not stop the rest; you get a result per database below.
                    </Callout>

                    {!results && pending.length > 0 && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                {plural(pending.length, "revision", "revisions")} this
                                will run, in order
                            </div>
                            <RevisionList revisions={pending} max={8} />
                            {/* Raw SQL across the whole run. A list of schema
                                steps reads as the full story otherwise, and the
                                part it omits is the part that moves data. */}
                            {pendingRawSql > 0 && (
                                <div className="text-[11px] text-tone-violet mt-2">
                                    {plural(pendingRawSql, "step", "steps")} in this run{" "}
                                    {pendingRawSql === 1 ? "is" : "are"} raw SQL, so what
                                    they do to existing rows is not listed above.
                                </div>
                            )}
                        </div>
                    )}

                    {!results && !isAll && pending.length === 0 && (
                        <div className="text-[12px] text-text-secondary">
                            Nothing is pending on this database — it already carries
                            every migration in this build. Applying is a no-op.
                        </div>
                    )}

                    {!results && (
                        <div>
                            <label
                                htmlFor={confirmId}
                                className="block text-xs text-text-secondary mb-1"
                            >
                                Type{" "}
                                <span className="font-mono font-semibold text-text-primary">
                                    {expectedConfirm}
                                </span>{" "}
                                to confirm
                            </label>
                            <Input
                                id={confirmId}
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                disabled={applying}
                                className={CONFIRM_INPUT_CLASS}
                                placeholder={expectedConfirm}
                                autoComplete="off"
                            />
                        </div>
                    )}

                    {results && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                Results
                            </div>
                            <ResultList
                                results={results}
                                renderDetail={(r) => (
                                    <>
                                        {r.fromRevision || "?"} → {r.toRevision || "?"}
                                        {r.durationMs != null &&
                                            ` · ${(r.durationMs / 1000).toFixed(1)}s`}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {error && <ErrorNote>{error}</ErrorNote>}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                    <DialogClose
                        disabled={applying}
                        render={
                            <Button variant="outline" type="button" disabled={applying}>
                                {results ? "Close" : "Cancel"}
                            </Button>
                        }
                    />
                    {!results && (
                        <Button
                            onClick={onApplyClick}
                            disabled={!canApply}
                            className="bg-button-secondary text-text-base"
                        >
                            <Play size={14} className="mr-1" aria-hidden="true" />
                            {applying ? "Upgrading…" : "Upgrade"}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AlembicApplyModal;
